import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { useIamCatalog } from '../../hooks/useIam';
import { catalogLookups } from '../../components/access/iamLookups';
import type { CatalogBundleEntry, CatalogRoleEntry } from '../../services/iam';
import { AccessStateNotice } from '../../components/access/AccessStateNotice';
import { RolesList } from '../../components/access/roles/RolesList';
import type { RoleListTab } from '../../components/access/roles/RoleListTabs';
import { RoleDetailPanel } from '../../components/access/roles/RoleDetailPanel';

export function AccessRoles() {
    usePageTitle('Access · Roles');
    const { setTitle, setSubtitle } = usePageHeader();
    const { catalog, isLoading, isError, refresh } = useIamCatalog();

    const roles = useMemo(
        () =>
            (catalog?.roles ?? []).filter((role): role is CatalogRoleEntry => role.kind === 'role'),
        [catalog]
    );
    const bundles = useMemo(
        () =>
            (catalog?.roles ?? []).filter(
                (role): role is CatalogBundleEntry => role.kind === 'bundle'
            ),
        [catalog]
    );
    const { permissionLookup, roleLookup } = useMemo(() => catalogLookups(catalog), [catalog]);

    // `?role=` makes a selection deep-linkable - Permissions' "roles
    // containing this permission" list navigates here with it set.
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedKey = searchParams.get('role');

    const selectRole = useCallback(
        (key: string) => {
            const next = new URLSearchParams(searchParams);
            next.set('role', key);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        if (selectedKey || !catalog) return;
        // Roles first, bundles as fallback, so the page defaults to the Roles tab.
        const firstKey = roles[0]?.key ?? bundles[0]?.key;
        if (firstKey) selectRole(firstKey);
    }, [catalog, selectedKey, roles, bundles, selectRole]);

    useEffect(() => {
        setTitle('Roles');
        setSubtitle('Defined in code at backend/src/iam/roles/*.ts - read-only here');
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [setTitle, setSubtitle]);

    const selectedRole = catalog?.roles.find((role) => role.key === selectedKey) ?? null;
    // Derived from the selection's kind, not tracked as separate state, so a
    // deep link always lands on the tab that holds it.
    const activeTab: RoleListTab = selectedRole?.kind === 'bundle' ? 'bundles' : 'roles';

    const selectTab = useCallback(
        (tab: RoleListTab) => {
            const first = (tab === 'bundles' ? bundles : roles)[0];
            if (first) selectRole(first.key);
        },
        [roles, bundles, selectRole]
    );

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            {isError ? (
                <AccessStateNotice
                    title="Couldn't load the role catalog"
                    message="GET /api/v2/iam/catalog failed to load."
                    onRetry={() => void refresh()}
                />
            ) : isLoading || !catalog ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                </div>
            ) : (
                <div className="flex-1 min-h-0 flex gap-6 px-8 pb-8">
                    <aside className="w-[280px] shrink-0 min-h-0 flex flex-col">
                        <RolesList
                            roles={roles}
                            bundles={bundles}
                            activeTab={activeTab}
                            onTabChange={selectTab}
                            selectedKey={selectedKey}
                            onSelect={selectRole}
                        />
                    </aside>
                    {selectedRole ? (
                        <RoleDetailPanel
                            // Keying by selection forces a full remount on switch,
                            // so no stray state can survive a selection change.
                            key={selectedKey}
                            role={selectedRole}
                            allRoles={catalog.roles}
                            roleLookup={roleLookup}
                            permissionLookup={permissionLookup}
                            routes={catalog.routes}
                            onSelectRole={selectRole}
                        />
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                            Select a role
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
